import { Id } from "@cvx/_generated/dataModel";
import { Target } from "lucide-react";
import { truncateText } from "@/utils/format";

type AnyVideo = {
  _id: string | Id<"contractVideos">;
  url?: string;
  title?: string;
  views?: number;
  multiple?: number;
};

interface TopPerformersCardProps {
  videos: AnyVideo[];
}

export const TopPerformersCard = ({ videos }: TopPerformersCardProps) => {
  const calculateMedianAndTopPerformers = (videos: AnyVideo[]) => {
    if (!videos || videos.length === 0) {
      return {
        median: 0,
        topPerformers: [],
      };
    }

    // Get all view counts and sort them
    const viewCounts = videos
      .map((video) => (video.views as number) ?? 0)
      .filter((views) => views > 0)
      .sort((a, b) => a - b);

    if (viewCounts.length === 0) {
      return {
        median: 0,
        topPerformers: [],
      };
    }

    // Calculate median
    const median =
      viewCounts.length % 2 === 0
        ? (viewCounts[Math.floor(viewCounts.length / 2) - 1] +
            viewCounts[Math.floor(viewCounts.length / 2)]) /
          2
        : viewCounts[Math.floor(viewCounts.length / 2)];

    // Find videos with highest multiples of median
    const videosWithMultiples = videos
      .filter((video) => ((video.views as number) ?? 0) > 0)
      .map((video) => ({
        ...video,
        multiple: median > 0 ? ((video.views as number) ?? 0) / median : 0,
      }))
      .sort((a, b) => b.multiple - a.multiple)
      .slice(0, 5); // Get top 5

    return {
      median,
      topPerformers: videosWithMultiples,
    };
  };

  const { topPerformers } = calculateMedianAndTopPerformers(videos);

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-primary/60">
          Top Performers vs Median
        </h3>
        <Target className="h-5 w-5 text-primary/60" />
      </div>
      <div className="space-y-2">
        {topPerformers.length > 0 ? (
          <div className="space-y-2">
            {topPerformers.map((video) => (
              <div
                key={video._id}
                className="flex items-center justify-between text-sm"
              >
                <a
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline truncate flex-1 mr-2"
                  title={video.title}
                >
                  {truncateText(video.title ?? "No title")}
                </a>
                <span className="font-semibold text-primary">
                  {video.multiple.toFixed(1)}x
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-lg text-primary">No videos</p>
        )}
      </div>
    </div>
  );
};
